import type { Spend, SpendSummary, User } from "@fusioncraft/shared";
import { useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { Dialog } from "./Dialog";

function TwoFactor({ user, onUserChanged }: { user: User; onUserChanged: (u: User) => void }) {
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const begin = () => run(async () => setSetup(await api.totpSetup()));

  const enable = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      const { user } = await api.totpEnable(code);
      setSetup(null);
      setCode("");
      onUserChanged(user);
    });
  };

  const disable = (e: FormEvent) => {
    e.preventDefault();
    run(async () => {
      const { user } = await api.totpDisable(password, code);
      setDisabling(false);
      setCode("");
      setPassword("");
      onUserChanged(user);
    });
  };

  if (user.totpEnabled) {
    return (
      <section className="account-section">
        <div className="account-row">
          <div>
            <h3>Two-factor authentication</h3>
            <p className="status-on">On. Signing in asks for a code from your authenticator.</p>
          </div>
          {!disabling && (
            <button className="btn" onClick={() => setDisabling(true)}>
              Turn off
            </button>
          )}
        </div>
        {disabling && (
          <form className="stack" onSubmit={disable}>
            <label className="field">
              <span>Password</span>
              <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            <label className="field">
              <span>Current code</span>
              <input className="input input-code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required />
            </label>
            {error && <p className="form-error">{error}</p>}
            <div className="form-actions">
              <button type="button" className="btn" onClick={() => setDisabling(false)}>Cancel</button>
              <button className="btn btn-danger" disabled={busy}>Turn off 2FA</button>
            </div>
          </form>
        )}
      </section>
    );
  }

  return (
    <section className="account-section">
      <div className="account-row">
        <div>
          <h3>Two-factor authentication</h3>
          <p className="muted">Off. Add a second step with any TOTP authenticator app.</p>
        </div>
        {!setup && (
          <button className="btn" onClick={begin} disabled={busy}>
            Set up
          </button>
        )}
      </div>
      {setup && (
        <form className="totp-setup" onSubmit={enable}>
          <div className="totp-qr" dangerouslySetInnerHTML={{ __html: setup.qr }} />
          <div className="stack">
            <p>Scan this with your authenticator app, or enter the key by hand:</p>
            <code className="totp-secret">{setup.secret.match(/.{1,4}/g)?.join(" ")}</code>
            <label className="field">
              <span>Code from the app</span>
              <input className="input input-code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
            </label>
            {error && <p className="form-error">{error}</p>}
            <div className="form-actions">
              <button type="button" className="btn" onClick={() => setSetup(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={busy}>Turn on</button>
            </div>
          </div>
        </form>
      )}
      {!setup && error && <p className="form-error">{error}</p>}
    </section>
  );
}

function ChangePassword() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await api.changePassword(current, next);
      setMsg({ ok: true, text: "Password changed. Other devices were signed out." });
      setOpen(false);
      setCurrent("");
      setNext("");
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="account-section">
      <div className="account-row">
        <div>
          <h3>Password</h3>
          {msg?.ok ? <p className="status-on">{msg.text}</p> : <p className="muted">Changing it signs out your other devices.</p>}
        </div>
        {!open && <button className="btn" onClick={() => setOpen(true)}>Change</button>}
      </div>
      {open && (
        <form className="stack" onSubmit={submit}>
          <label className="field">
            <span>Current password</span>
            <input className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </label>
          <label className="field">
            <span>New password</span>
            <input className="input" type="password" autoComplete="new-password" minLength={10} value={next} onChange={(e) => setNext(e.target.value)} required />
            <small>At least 10 characters.</small>
          </label>
          {msg && !msg.ok && <p className="form-error">{msg.text}</p>}
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy}>Save password</button>
          </div>
        </form>
      )}
    </section>
  );
}

/** Small amounts need more digits: $0.000137 rather than $0.00. */
function usd(n: number): string {
  if (n === 0) return "$0.00";
  return `$${n.toFixed(n < 0.001 ? 6 : n < 0.01 ? 5 : n < 1 ? 4 : 2)}`;
}

function SpendSection() {
  const [spend, setSpend] = useState<SpendSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.spend().then(setSpend, (e) => setError(e.message));
  }, []);

  // Longer periods only appear when they add something over the shorter one before them.
  const rows: [string, Spend][] = [];
  if (spend) {
    const periods: [string, Spend][] = [
      ["Today", spend.today],
      ["This month", spend.month],
      ["This year", spend.year],
      ["All time", spend.total],
    ];
    for (const [label, s] of periods) {
      const prev = rows[rows.length - 1]?.[1];
      if (!prev || s.calls !== prev.calls || s.costUsd !== prev.costUsd) rows.push([label, s]);
    }
  }

  return (
    <section className="account-section">
      <div>
        <h3>Spend on inference</h3>
        <p className="muted">Exact cost reported by OpenRouter. Cached fusions are free.</p>
      </div>
      {error && <p className="form-error">{error}</p>}
      {rows.length > 0 && (
        <dl className="spend">
          {rows.map(([label, s]) => (
            <div key={label} className="spend-row">
              <dt>{label}</dt>
              <dd>
                <strong>{usd(s.costUsd)}</strong>
                <span className="muted">
                  {s.calls} {s.calls === 1 ? "call" : "calls"}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

export function AccountDialog({
  user,
  onClose,
  onUserChanged,
  onSignOut,
}: {
  user: User;
  onClose: () => void;
  onUserChanged: (u: User) => void;
  onSignOut: () => void;
}) {
  return (
    <Dialog title="Account" onClose={onClose}>
      <section className="account-section">
        <div className="account-row">
          <div>
            <h3>{user.email}</h3>
            <p className="muted">Member since {new Date(user.createdAt).toLocaleDateString()}</p>
          </div>
          <button className="btn" onClick={onSignOut}>Sign out</button>
        </div>
      </section>
      <SpendSection />
      <ChangePassword />
      <TwoFactor user={user} onUserChanged={onUserChanged} />
    </Dialog>
  );
}
